import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  SvgIcon,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

import { useSelector } from 'react-redux';
import CardHeader from '../../../../../components/General/CardHeader';
import CustomButton from '../../../../../components/General/CustomButton';
import CustomModal from '../../../../../components/General/CustomModal';
import CustomTable from '../../../../../components/General/CustomTable';
import { UserRole, UserType } from '../../../../../constants/user_types';
import apiClient from '../../../../../requests/api/apiClient';
import {
  CultivationCyclePopulatedSchema,
  PhenologicalStateWithCycle,
  PostCultivationCycleSchema,
  UpdateCultivationCycleSchema,
  VarietySchema,
} from '../../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../../types';
import { ignoreDateTimezone } from '../../../../../utils/formatDate';
import { FetchError } from '../../../../types';
import {
  FIELDS_EQ,
  ModalUpsertCultivationCycleSchema,
  SECTOR_LIST_UI,
  SimplifiedCultivationCycle,
} from '../../types';
import useStyles from '../styles';
import UpsertModalCultivationCycleContent from './UpsertCultivationCycleModalContent';

function CultivationCyclesList({
  cultivationCycles,
  sectorId,
  varietiesList,
  speciesList,
  setCultivationCyclesFunction,
  setPhenologicalStatesWCycle,
  setRefreshPhenologicalStates,
}: {
  cultivationCycles: SimplifiedCultivationCycle[];
  sectorId: string;
  varietiesList: VarietySchema[];
  speciesList: { id?: string; name?: string }[];
  setCultivationCyclesFunction:
  React.Dispatch<React.SetStateAction<CultivationCyclePopulatedSchema[]>>;
  setPhenologicalStatesWCycle: (
    updater: (prev: PhenologicalStateWithCycle) => PhenologicalStateWithCycle) => void;
  setRefreshPhenologicalStates: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const classes = useStyles();
  const [upsertModal, setUpsertModal] = useState({ cycleId: '', open: false });
  const [
    localCultivationCycles, setLocalCultivationCycles,
  ] = useState<SimplifiedCultivationCycle[]>(cultivationCycles);
  const { user } = useSelector((state: ReduxState) => state.account);

  const isAdmin = user?.role === UserRole.ADMIN && user?.type === UserType.CDTEC;
  const isOperator = user?.role === UserRole.OPERATOR && user?.type === UserType.CDTEC;

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setLocalCultivationCycles(cultivationCycles);
  }, [cultivationCycles]);

  const datesOverlapWithExisting = (
    startDate: string,
    endDate: string,
    cycles: SimplifiedCultivationCycle[],
    excludeId?: string,
  ): boolean => (
    cycles.some((cycle) => (
      (excludeId ? cycle.id !== excludeId : true)
      && startDate <= ignoreDateTimezone(cycle.endDate)
      && endDate >= ignoreDateTimezone(cycle.startDate)
    ))
  );

  const updateCultivationCycle = async (
    cultivationCycle: UpdateCultivationCycleSchema & { sectorId: string },
  ) => {
    const response = await apiClient.cultivationCycle.cultivationCycleUpdate(cultivationCycle);

    const updatedCultivationCycle = response.data.updatedCycle?.cultivationCycle;
    const updatedPhenologicalStates = response.data.updatedCycle?.phenologicalStates;

    if (updatedCultivationCycle) {
      const variety = varietiesList.find((v) => v.id === updatedCultivationCycle.variety);

      setCultivationCyclesFunction((prevCycles) => prevCycles.map(
        (cycle) => (cycle.id === upsertModal.cycleId ? {
          ...cycle,
          id: updatedCultivationCycle.id,
          name: updatedCultivationCycle.name,
          variety: {
            name: variety?.name || '',
            species: variety?.species || '',
          },
          startDate: new Date(updatedCultivationCycle.startDate).toISOString(),
          endDate: new Date(updatedCultivationCycle.endDate).toISOString(),
          producedKg: updatedCultivationCycle.producedKg,
          exportablePercentage:
            updatedCultivationCycle.exportablePercentage,
          productionQuality:
            updatedCultivationCycle.productionQuality,
        } : cycle),
      ));

      if (updatedPhenologicalStates && updatedPhenologicalStates.length > 0) {
        setPhenologicalStatesWCycle((prev) => ({
          ...prev,
          [updatedCultivationCycle.id]: updatedPhenologicalStates,
        }));
      }
    }

    // Force refresh of phenological states data
    setRefreshPhenologicalStates((prev) => !prev);

    return response.data.message;
  };

  const createCultivationCycle = async (cultivationCycle: PostCultivationCycleSchema) => {
    const response = await apiClient.cultivationCycle.cultivationCycleCreate(cultivationCycle);
    const newCultivationCycle = response.data.cultivationCycle?.cultivationCycle;
    const newPhenologicalStates = response.data.cultivationCycle?.phenologicalStates;

    if (newCultivationCycle) {
      const variety = varietiesList.find((v) => v.id === newCultivationCycle.variety);

      setCultivationCyclesFunction((prevCycles) => [...prevCycles, {
        id: newCultivationCycle.id,
        name: newCultivationCycle.name,
        variety: {
          name: variety?.name || '',
          species: variety?.species || '',
        },
        startDate: new Date(newCultivationCycle.startDate).toISOString(),
        endDate: new Date(newCultivationCycle.endDate).toISOString(),
        phenologicalStates: [],
        producedKg: newCultivationCycle.producedKg,
        exportablePercentage:
          newCultivationCycle.exportablePercentage,
        productionQuality:
          newCultivationCycle.productionQuality,
      }]);

      if (newPhenologicalStates && newPhenologicalStates.length > 0) {
        setPhenologicalStatesWCycle((prev) => ({
          ...prev,
          [newCultivationCycle.id]: newPhenologicalStates,
        }));
      }
    }

    // Force refresh of phenological states data
    setRefreshPhenologicalStates((prev) => !prev);

    return response.data.message;
  };

  const handleUpsertCultivationCycle = async (
    cultivationCycle: ModalUpsertCultivationCycleSchema,
  ) => {
    try {
      let responseMessage;

      if (upsertModal.cycleId) {
        const updateCycle = cultivationCycle as UpdateCultivationCycleSchema;
        updateCycle.id = upsertModal.cycleId;
        const hasMissingFields = Object.keys(updateCycle).some((key) => {
          const value = updateCycle[key as keyof UpdateCultivationCycleSchema];
          if (value === undefined || value === null || value === '') {
            enqueueSnackbar(`El campo ${FIELDS_EQ[key as keyof UpdateCultivationCycleSchema]} debe ser completado`, { variant: 'error' });
            return true;
          }
          return false;
        });
        if (hasMissingFields) return;
        if (!updateCycle.startDate || !updateCycle.endDate) {
          enqueueSnackbar('Las fechas de inicio y fin son requeridas', { variant: 'error' });
          return;
        }
        const overlapExists = datesOverlapWithExisting(
          updateCycle.startDate,
          updateCycle.endDate,
          localCultivationCycles,
          updateCycle.id,
        );
        if (overlapExists) {
          enqueueSnackbar('Las fechas se traslapan con otro ciclo del sector', { variant: 'error' });
          return;
        }

        responseMessage = await updateCultivationCycle({ ...updateCycle, sectorId });
      } else {
        const postCycle = cultivationCycle as PostCultivationCycleSchema;
        postCycle.sectorId = sectorId;
        const hasMissingFields = Object.keys(postCycle).some((key) => {
          const value = postCycle[key as keyof PostCultivationCycleSchema];
          if (value === undefined || value === null || value === '') {
            enqueueSnackbar(`El campo ${FIELDS_EQ[key as keyof PostCultivationCycleSchema]} debe ser completado`, { variant: 'error' });
            return true;
          }
          return false;
        });
        if (hasMissingFields) return;

        const overlapExists = datesOverlapWithExisting(
          postCycle.startDate,
          postCycle.endDate,
          localCultivationCycles,
        );
        if (overlapExists) {
          enqueueSnackbar('Las fechas se traslapan con otro ciclo del sector', { variant: 'error' });
          return;
        }

        responseMessage = await createCultivationCycle(postCycle);
      }

      setUpsertModal({ cycleId: '', open: false });
      enqueueSnackbar(responseMessage, { variant: 'success' });
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const setCultivationCycleIdToModal = (cycleId: string) => {
    const cycleToEdit = localCultivationCycles.find((cycle) => cycle.id === cycleId);
    if (cycleToEdit) {
      setUpsertModal({ cycleId, open: true });
    }
  };

  const handleRemoveCultivationCycle = async (id: string) => {
    try {
      const response = await apiClient.cultivationCycle.cultivationCycleDelete(id);
      setCultivationCyclesFunction((prevCycles) => prevCycles.filter((cycle) => cycle.id !== id));
      enqueueSnackbar(response.data.message, { variant: 'success' });
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const localData = {
    count: localCultivationCycles.length,
    values: localCultivationCycles.map((cycle) => {
      type RowData = {
        id: string;
        name: string;
        startingDate: string;
        endingDate: string;
        specie: string;
        variety: string;
        producedKg: string;
        exportablePercentage: string;
        productionQuality: string;
        actions?: JSX.Element;
      };

      const rowData: RowData = {
        id: uuidv4(),
        name: cycle.name,
        startingDate: ignoreDateTimezone(cycle.startDate),
        endingDate: ignoreDateTimezone(cycle.endDate),
        specie: cycle.varietySpecies,
        variety: cycle.varietyName,
        producedKg: cycle.producedKg != null
          ? String(cycle.producedKg) : '-',
        exportablePercentage: cycle.exportablePercentage != null
          ? `${cycle.exportablePercentage}%` : '-',
        productionQuality: cycle.productionQuality != null
          ? String(cycle.productionQuality) : '-',
      };

      if (isAdmin || isOperator) {
        rowData.actions = (
          <>
            <IconButton onClick={() => setCultivationCycleIdToModal(cycle.id)}>
              <SvgIcon fontSize="small">
                <EditIcon />
              </SvgIcon>
            </IconButton>
            <IconButton size="large" onClick={() => handleRemoveCultivationCycle(cycle.id)}>
              <SvgIcon fontSize="small">
                <DeleteIcon />
              </SvgIcon>
            </IconButton>
          </>
        );
      }

      return rowData;
    }),
  };

  return (
    <>
      <Box mt={3} mb={3}>
        <Card>
          <CardContent>
            <CardHeader
              title='Ciclo de cultivo'
              ActionButton={
                <Box width="20em">
                  {(isAdmin || isOperator) && (
                    <CustomButton
                      buttonText='Agregar ciclo de cultivo'
                      color='blue'
                      size='small'
                      onClick={() => setUpsertModal({ cycleId: '', open: true })}
                    />
                  )}
                </Box>
              }
            />
            <Divider className={classes.divider} />
            <CustomTable
              rows={localData}
              headers={SECTOR_LIST_UI(isAdmin || isOperator)}
            />
          </CardContent>
        </Card>
      </Box>
      <CustomModal open={upsertModal.open} handleClose={() => setUpsertModal({ cycleId: '', open: false })} data={{ id: upsertModal.cycleId }}>
        <UpsertModalCultivationCycleContent
          handleCloseModal={() => setUpsertModal({ cycleId: '', open: false })}
          handleUpsertCycleFunction={handleUpsertCultivationCycle}
          varietiesList={varietiesList}
          speciesList={speciesList}
          cycleId={upsertModal.cycleId}
          cultivationCycle={localCultivationCycles.find(
            (cycle) => cycle.id === upsertModal.cycleId,
          )}
        />
      </CustomModal>
    </>
  );
}

export default CultivationCyclesList;
